import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaChartLine, FaBoxOpen, FaArrowsRotate, FaUsers, FaFileInvoice } from 'react-icons/fa6';
import { buildEffyOrderSchema } from '../seo/seoSchemas';
import { FaqSection } from '../seo/SeoShared';
import { EFFY_ORDER_FAQS } from './effyOrderFaqs';
import './EffyOrder.css';
import '../seo/SeoPages.css';

// Built from the same list the visible FAQ section renders, so they cannot drift.
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: EFFY_ORDER_FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
};

const FEATURES = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    image: '/images/dashboard.PNG',
    title: 'Your Command Center for Growth',
    description: 'A dedicated, intelligent dashboard that gives you a bird’s-eye view of your entire business performance. Stop guessing and start knowing with real-time financial tracking and dynamic visual graphs.',
    points: [
      'Easily track your orders month by month and discover busy seasons at a glance.',
      'Stay on top of daily sales activity with clear and simple order insights.'
    ],
  },
  {
    id: 'orders',
    label: 'Orders',
    images: [
      { src: '/images/order3.PNG', alt: 'Order items table' },
      { src: '/images/OrderUI.PNG', alt: 'Bulk upload orders' },
      { src: '/images/order2.PNG', alt: 'Order type selector' },
      { src: '/images/Order1.PNG', alt: 'Add order buttons' },
    ],
    title: 'Streamlined Order Mastery',
    description: 'Transform how you handle fulfillment. From single entries to massive bulk uploads, our system ensures you never drop an order and your customers are always in the loop.',
    points: [
      'Manage every order smoothly — whether it’s a single order or thousands uploaded in minutes.',
      'Keep complete control from order creation to successful delivery without missing a step.',
      'Automatically keep customers informed through WhatsApp and email'
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    images: [
      { src: '/images/InventoryDetail.PNG', alt: 'Inventory item detail' },
      { src: '/images/InventoryUI.PNG', alt: 'Bulk upload inventory' },
      { src: '/images/Inventory1.PNG', alt: 'Add item options' },
      { src: '/images/Inventory2.PNG', alt: 'Barcode print preview' },
    ],
    title: 'Automated Stock Intelligence',
    description: 'Create your inventory once and let our system do the heavy lifting. Never oversell, never run out of stock unexpectedly, and maintain perfect sync across all operations.',
    points: [
      'Organize your products once and create orders faster without repeating the same work.',
      'Always know what’s available in stock and avoid confusion or missing items.',
      'Get notified before products run low so you can restock on time.',
      'Make warehouse handling simple with easy barcode generation and printing.',
      'Quickly find any product whenever you need it.'
    ],
  },
  {
    id: 'customer',
    label: 'Customer',
    image: '/images/Customer.PNG',
    title: 'Effortless Customer Relationships',
    description: 'Build a powerful customer database automatically. We save you time by learning your customers and predicting their needs, giving you a 360-degree view of their purchasing history.',
    points: [
      'Easily view and track every customer’s order history in one place',
      'Understand your customers better by seeing what they buy most and how often they order.',
      'Strengthen customer relationships with faster service and a more personalized experience.'
    ],
  },
  {
    id: 'billings',
    label: 'Billings',
    image: '/images/Billing.PNG',
    imageZoom: true,
    title: 'Professional, Instant Documentation',
    description: 'Create professional billing documents in seconds without manual work. Instantly generate clean and GST-ready invoices for your customers.',
    points: [
      'Share estimate bills quickly for approvals before confirming orders.',
      'Simplify deliveries with easy-to-print delivery challans for your team.',
      'Give your business a more professional and organized customer experience.'
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    image: '/images/GSTReports.PNG',
    title: 'Deep Business Analytics',
    description: 'Turn your raw data into actionable insights. Export exactly what you need for your accountant, tax filings, or internal strategy meetings without wrestling with spreadsheets.',
    points: [
      'Comprehensive GST reports for hassle-free tax compliance.',
      'Detailed Sales reports to understand revenue streams over time.',
      'Customer-wise sales reports to identify and reward your most valuable clients.'
    ],
  }
];

export default function OrdersInventoryPage() {
  const location = useLocation();

  useEffect(() => {
    // Handle hash scrolling if navigating with a hash (e.g. #pricing)
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="oi-page-v2">
      <Helmet>
        <title>Order Management Tool &amp; Inventory Management Software for Small Businesses | EffyVenture</title>
        <meta name="description" content="Order management tool and inventory management software for small businesses. Track orders, auto-sync stock, generate GST invoices, print delivery challans, and view sales analytics — all in one platform." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.effyventure.com/products/effy-order" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.effyventure.com/products/effy-order" />
        <meta property="og:site_name" content="EffyVenture" />
        <meta property="og:title" content="Order Management Tool &amp; Inventory Management Software | EffyVenture" />
        <meta property="og:description" content="Order management tool for small businesses. Track orders, manage stock, generate GST invoices, print barcodes and delivery challans — start a 30-day free trial of Effy Orders." />
        <meta property="og:image" content="https://www.effyventure.com/logo/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Order Management Tool &amp; Inventory Management Software | EffyVenture" />
        <meta name="twitter:description" content="Order management tool and inventory management software for small businesses. GST invoices, stock alerts, bulk uploads, and sales reports." />
        <meta name="twitter:image" content="https://www.effyventure.com/logo/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(buildEffyOrderSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="oi-hero-v2">
        <div className="oi-hero-v2__container">
          <div className="oi-hero-v2__content">
            <div className="oi-badge-v2">Introducing Effy Orders</div>
            <h1 className="oi-hero-v2__title">
              Simplify Billing, Inventory & Order Management
              for Growing Retail and Wholesale Businesses <br />
              <span className="text-gradient">all in one place.</span>
            </h1>
            <p className="oi-hero-v2__subtitle">
              From bulk order processing to real-time inventory sync and automated customer updates, our platform helps you streamline operations, reduce manual work, and scale with confidence.
            </p>
            <ul className="oi-hero-v2__features-list">
              <li><span>✔</span> Smart Order Management</li>
              <li><span>✔</span> Auto Inventory Tracking</li>
              <li><span>✔</span> WhatsApp &amp; Email Notifications</li>
              <li><span>✔</span> GST &amp; Sales Reports</li>
              <li><span>✔</span> Invoice &amp; Barcode Printing</li>
              <li><span>✔</span> Customer Order History</li>
            </ul>
            <div className="oi-hero-v2__actions">
              <Link to="/products/order-tracking/signup" className="btn-v2 btn-v2--primary">
                Try it free
              </Link>
              <Link to="/products/order-tracking/login" className="btn-v2 btn-v2--primary">
                Sign In
              </Link>
            </div>
          </div>

          <div className="oi-hero-v2__visual">
            <div className="oi-hero-v2__image-wrapper">
              <div className="oi-hero-v2__floating-badge oi-hero-v2__floating-badge--sales">
                <span className="oi-hero-badge-icon"><FaChartLine /></span>
                <div className="oi-hero-badge-text">
                  <strong>Sales</strong>
                  <span>+24% this week</span>
                </div>
              </div>
              <div className="oi-hero-v2__floating-badge oi-hero-v2__floating-badge--purchases">
                <span className="oi-hero-badge-icon"><FaBoxOpen /></span>
                <div className="oi-hero-badge-text">
                  <strong>Purchases</strong>
                  <span>Auto-synced</span>
                </div>
              </div>
              <div className="oi-hero-v2__floating-badge oi-hero-v2__floating-badge--stock">
                <span className="oi-hero-badge-icon"><FaArrowsRotate /></span>
                <div className="oi-hero-badge-text">
                  <strong>Stock Sync</strong>
                  <span>Real-time</span>
                </div>
              </div>
              <div className="oi-hero-v2__floating-badge oi-hero-v2__floating-badge--customer">
                <span className="oi-hero-badge-icon"><FaUsers /></span>
                <div className="oi-hero-badge-text">
                  <strong>Order History</strong>
                  <span>Customer-wise</span>
                </div>
              </div>
              <div className="oi-hero-v2__floating-badge oi-hero-v2__floating-badge--gst">
                <span className="oi-hero-badge-icon"><FaFileInvoice /></span>
                <div className="oi-hero-badge-text">
                  <strong>GST Reports</strong>
                  <span>Auto-generated</span>
                </div>
              </div>
              <img
                src="/images/heroImage.png"
                alt="Orders & Inventory dashboard"
                className="oi-hero-v2__img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Sections */}
      <div className="oi-features-wrapper">
        {FEATURES.map((feature, idx) => {
          const isReverse = idx % 2 !== 0;
          return (
            <section key={feature.id} className={`oi-feature-v2 ${isReverse ? 'oi-feature-v2--reverse' : ''}`}>
              <div className="oi-feature-v2__container">
                <div className="oi-feature-v2__content">
                  <span className="oi-feature-v2__label">{feature.label}</span>
                  <h2 className="oi-feature-v2__title">{feature.title}</h2>
                  <p className="oi-feature-v2__desc">{feature.description}</p>
                  <ul className="oi-feature-v2__list">
                    {feature.points.map((point, i) => (
                      <li key={i}>
                        <svg className="oi-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="oi-feature-v2__visual">
                  {feature.images ? (
                    <div className="oi-collage">
                      <img src={feature.images[0].src} alt={feature.images[0].alt} className="oi-collage__top" />
                      <div className="oi-collage__bottom">
                        <img src={feature.images[1].src} alt={feature.images[1].alt} className="oi-collage__main" />
                        <div className="oi-collage__stack">
                          <img src={feature.images[2].src} alt={feature.images[2].alt} className="oi-collage__sm" />
                          <img src={feature.images[3].src} alt={feature.images[3].alt} className="oi-collage__sm" />
                        </div>
                      </div>
                    </div>
                  ) : feature.image ? (
                    feature.imageZoom ? (
                      <div className="oi-img-zoom-container">
                        <img
                          src={feature.image}
                          alt={`${feature.label} screenshot`}
                          className="oi-feature-v2__img--zoomed"
                        />
                      </div>
                    ) : (
                      <img
                        src={feature.image}
                        alt={`${feature.label} screenshot`}
                        className="oi-feature-v2__img"
                      />
                    )
                  ) : (
                    <div className="oi-image-placeholder">
                      <div className="oi-image-placeholder__inner">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                        <p>Image Placeholder: <strong>{feature.label} UI</strong></p>
                        <span className="oi-image-placeholder__hint">(Add your screenshot here)</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* Pricing CTA */}
      <section className="oi-pricing-cta">
        <div className="oi-pricing-cta__inner">
          <h2 className="oi-pricing-cta__title">Simple, transparent pricing</h2>
          <p className="oi-pricing-cta__subtitle">
            Plans starting at ₹199/mo with a 30-day free trial. No hidden fees, no surprises — upgrade anytime as you grow.
          </p>
          <Link to="/effy-order-pricing" className="btn-v2 btn-v2--primary">
            View Pricing Plans
          </Link>
        </div>
      </section>

      <FaqSection faqs={EFFY_ORDER_FAQS} />
    </div>
  );
}
